import { Link } from "@tanstack/react-router";
import type { PluginSkillPermissions } from "@cmmd-forge/contracts/settings";
import { ArrowRightIcon, BoxIcon, ExternalLinkIcon, MessageCircleIcon } from "lucide-react";

import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { PluginConnectionAction } from "./PluginConnectionStatus";
import { PluginIcon } from "./pluginIcons";
import { pluginDetailMetadata, type PluginDetailMetadata } from "./pluginMetadata";
import { isPluginSkillEnabled, setPluginSkillEnabled } from "./pluginPermissions";
import {
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "./pluginsPage.logic";
import { usePluginConnections } from "./PluginConnectionsProvider";
import { pluginConnectionCapabilities } from "./pluginConnections";
import { useSettings, useUpdateSettings } from "~/hooks/useSettings";

interface PluginDetailPageProps {
  readonly pluginId: ForgePluginId;
}

export function PluginDetailPage({ pluginId }: PluginDetailPageProps) {
  const { plugins, initialized, error, attempts, connect, disconnect } = usePluginConnections();
  const pluginSkillPermissions = useSettings((settings) => settings.pluginSkillPermissions);
  const { updateSettings } = useUpdateSettings();
  const metadata = pluginDetailMetadata(pluginId);
  const plugin = plugins.find((entry) => entry.id === pluginId) ?? metadataToPlugin(metadata);
  const handleSkillEnabledChange = (skillId: string, enabled: boolean) => {
    updateSettings({
      pluginSkillPermissions: setPluginSkillEnabled(
        pluginSkillPermissions,
        pluginId,
        skillId,
        enabled,
      ),
    });
  };

  return (
    <main className="flex h-[calc(100dvh-var(--forge-app-top-bar-height))] min-h-0 min-w-0 flex-1 flex-col overflow-auto bg-background">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8 px-5 py-5 sm:px-8 lg:px-10">
        <PluginDetailHeader metadata={metadata} />
        <PluginPromptHero metadata={metadata} />
        <p className="max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base">
          {metadata.body}
        </p>
        {error ? (
          <p className="text-sm text-muted-foreground">{error}. Refresh Plugins and try again.</p>
        ) : null}
        <PluginAppsSection
          metadata={metadata}
          plugin={plugin}
          attempt={attempts[pluginId] ?? null}
          isChecking={!initialized}
          onConnect={connect}
          onDisconnect={disconnect}
        />
        <PluginSkillsSection
          metadata={metadata}
          permissions={pluginSkillPermissions}
          onSkillEnabledChange={handleSkillEnabledChange}
        />
        <PluginInformationSection metadata={metadata} />
      </div>
    </main>
  );
}

function metadataToPlugin(metadata: PluginDetailMetadata): ForgePlugin {
  return {
    id: metadata.id,
    name: metadata.title,
    description: metadata.appDescription,
    status: "pending",
    authReady: false,
    authReason: "not_installed",
    connectionKind: metadata.id === "github" ? "github" : "cmmdConnector",
    capabilities: { connect: false, disconnect: false },
  };
}

interface PluginDetailHeaderProps {
  readonly metadata: PluginDetailMetadata;
}

function PluginDetailHeader({ metadata }: PluginDetailHeaderProps) {
  return (
    <header className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
      <div className="space-y-4">
        <PluginIcon pluginId={metadata.id} className="size-14" />
        {/* No title: the bar's leaf crumb reads from the same
            `pluginDetailMetadata(id).title`. The icon and tagline carry the hero. */}
        <div className="space-y-1">
          <p className="text-base text-muted-foreground">{metadata.tagline}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="white" render={<Link to="/new" />}>
          <MessageCircleIcon className="size-4" />
          Try in chat
        </Button>
      </div>
    </header>
  );
}

interface PluginPromptHeroProps {
  readonly metadata: PluginDetailMetadata;
}

function PluginPromptHero({ metadata }: PluginPromptHeroProps) {
  return (
    <section className="flex min-h-32 items-center justify-center rounded-lg bg-[radial-gradient(circle_at_20%_15%,rgba(45,87,171,0.55),transparent_38%),radial-gradient(circle_at_82%_12%,rgba(91,70,155,0.45),transparent_34%),linear-gradient(135deg,rgba(25,45,92,0.9),rgba(43,37,86,0.9))] px-5 py-8">
      <div className="flex w-full max-w-2xl items-center gap-2 rounded-xl bg-background/90 px-4 py-3 text-sm text-foreground shadow-lg">
        <PluginIcon pluginId={metadata.id} className="size-4" />
        <span className="shrink-0 font-medium">{metadata.promptLabel}</span>
        <span className="min-w-0 flex-1 truncate text-muted-foreground">{metadata.prompt}</span>
        <span className="ml-2 flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
          <ArrowRightIcon className="size-4" />
        </span>
      </div>
    </section>
  );
}

interface PluginAppsSectionProps {
  readonly metadata: PluginDetailMetadata;
  readonly plugin: ForgePlugin;
  readonly attempt: PluginConnectionAttempt | null;
  readonly isChecking: boolean;
  readonly onConnect: (pluginId: ForgePluginId) => void;
  readonly onDisconnect: (pluginId: ForgePluginId) => void;
}

function PluginAppsSection({
  metadata,
  plugin,
  attempt,
  isChecking,
  onConnect,
  onDisconnect,
}: PluginAppsSectionProps) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-medium text-muted-foreground">Apps&nbsp; 1</h2>
      <div className="grid min-h-20 items-center gap-4 rounded-lg bg-muted/35 px-4 py-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
        <div className="flex size-11 items-center justify-center rounded-md border border-border/80 bg-card shadow-sm">
          <PluginIcon pluginId={metadata.id} className="size-6" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-base font-medium text-foreground">{metadata.title}</p>
          <p className="truncate text-sm text-muted-foreground">{metadata.appDescription}</p>
        </div>
        <PluginConnectionAction
          plugin={plugin}
          attempt={attempt}
          isChecking={isChecking}
          onConnect={onConnect}
          onDisconnect={
            (plugin.capabilities ?? pluginConnectionCapabilities(plugin)).disconnect
              ? onDisconnect
              : undefined
          }
        />
      </div>
    </section>
  );
}

interface PluginSkillsSectionProps {
  readonly metadata: PluginDetailMetadata;
  readonly permissions: PluginSkillPermissions;
  readonly onSkillEnabledChange: (skillId: string, enabled: boolean) => void;
}

function PluginSkillsSection({
  metadata,
  permissions,
  onSkillEnabledChange,
}: PluginSkillsSectionProps) {
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-medium text-muted-foreground">
        Skills&nbsp; {metadata.skills.length}
      </h2>
      <div className="space-y-6">
        {metadata.skills.map((skill) => (
          <div
            key={skill.id}
            className="grid min-h-12 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4"
          >
            <SkillIcon pluginId={metadata.id} icon={skill.icon} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{skill.name}</p>
              <p className="truncate text-sm text-muted-foreground">{skill.description}</p>
            </div>
            <Switch
              checked={isPluginSkillEnabled(permissions, metadata.id, skill.id)}
              aria-label={`${skill.name} enabled`}
              onCheckedChange={(checked) => onSkillEnabledChange(skill.id, checked)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

interface SkillIconProps {
  readonly pluginId: ForgePluginId;
  readonly icon: "brand" | "skill";
}

function SkillIcon({ pluginId, icon }: SkillIconProps) {
  return icon === "brand" ? (
    <PluginIcon pluginId={pluginId} className="size-6 opacity-80" />
  ) : (
    <BoxIcon className="size-6 text-muted-foreground" />
  );
}

interface PluginInformationSectionProps {
  readonly metadata: PluginDetailMetadata;
}

function PluginInformationSection({ metadata }: PluginInformationSectionProps) {
  return (
    <section className="space-y-5 pb-10">
      <h2 className="text-sm font-medium text-foreground">Information</h2>
      <dl className="grid max-w-xl grid-cols-[minmax(9rem,14rem)_minmax(0,1fr)] gap-x-8 gap-y-5 text-sm">
        <dt className="text-muted-foreground">Capabilities</dt>
        <dd className="text-foreground">{metadata.capabilities}</dd>
        <dt className="text-muted-foreground">Access</dt>
        <dd className="text-foreground">{metadata.access}</dd>
        <dt className="text-muted-foreground">Authentication</dt>
        <dd className="text-foreground">{metadata.auth}</dd>
        <dt className="text-muted-foreground">Managed by</dt>
        <dd className="text-foreground">{metadata.managedBy}</dd>
        <dt className="text-muted-foreground">Provider</dt>
        <dd className="text-foreground">{metadata.provider}</dd>
        <dt className="text-muted-foreground">Category</dt>
        <dd className="text-foreground">{metadata.category}</dd>
        <InfoLink label="Provider app" href={metadata.providerUrl} />
      </dl>
    </section>
  );
}

interface InfoLinkProps {
  readonly label: string;
  readonly href: string;
}

function InfoLink({ label, href }: InfoLinkProps) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex text-foreground outline-none hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ExternalLinkIcon className="size-4" />
        </a>
      </dd>
    </>
  );
}
